import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Building2, MapPin, Loader2, AlertCircle, BadgeCheck } from "lucide-react";

interface Club {
  id: string;
  name: string;
  country: string;
  city: string | null;
  verified: boolean;
}

interface Props {
  clubId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClubDetailDialog({ clubId, open, onOpenChange }: Props) {
  const { data, isLoading, isError } = useQuery<Club>({
    queryKey: ["/api/clubs", clubId],
    enabled: open && !!clubId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-club-detail"
      >
        <DialogHeader>
          <DialogTitle>Club</DialogTitle>
          <DialogDescription>Información del club seleccionado.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError || !data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <AlertCircle className="h-4 w-4" />
            <span>No se pudo cargar el club.</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Building2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p
                    className="font-bold truncate"
                    data-testid="text-club-detail-name"
                  >
                    {data.name}
                  </p>
                  {data.verified ? (
                    <BadgeCheck
                      className="h-4 w-4 text-blue-500 shrink-0"
                      aria-label="Verificado"
                    />
                  ) : null}
                </div>
              </div>
            </div>
            {(data.city || data.country) ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0" />
                <span data-testid="text-club-detail-location">
                  {[data.city, data.country].filter(Boolean).join(", ")}
                </span>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
